import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { chipGlow, inr } from "@/lib/format";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BatteryWarning, Pencil, Check, X, Loader2 } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

export default function UnitCard({ unit, onUpdated }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canPrice = user?.role === "owner" || user?.role === "manager";
  const glow = chipGlow(unit.chip);
  const batt = unit.battery_capacity ? `${unit.battery_capacity}%` : "—";
  const serviceDue = unit.battery_condition && unit.battery_condition !== "Normal";

  const [editingPrice, setEditingPrice] = useState(false);
  const [value, setValue] = useState(unit.price ?? "");
  const [saving, setSaving] = useState(false);

  const savePrice = async (e) => {
    e?.stopPropagation?.();
    setSaving(true);
    try {
      const price = value === "" ? null : Number(value);
      const { data } = await api.patch(`/units/${unit.id}`, { price });
      toast.success(`${unit.stock_code} priced`);
      setEditingPrice(false);
      onUpdated?.(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`mac-unit-card-${unit.stock_code}`}
      onClick={() => navigate(`/unit/${unit.id}`)}
      className="group relative cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-[#121214] p-4 text-left transition-transform duration-150 active:scale-[0.98]"
    >
      <div className="pointer-events-none absolute -top-16 right-0 h-40 w-40 rounded-full opacity-40 blur-3xl" style={{ background: glow }} />
      <div className="relative">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-xs text-[#98989d]">{unit.stock_code}</span>
          <StatusBadge status={unit.status} testid={`status-badge-${unit.stock_code}`} />
        </div>

        <div className="text-xs uppercase tracking-wide text-[#98989d]">{unit.family}</div>
        <div className="mt-0.5 text-xl font-extrabold tracking-tight">
          {unit.size} <span style={{ color: glow }}>{unit.chip}</span>
        </div>

        <div className="mt-3 grid grid-cols-4 gap-2 border-t border-white/5 pt-3 text-center">
          <Spec label="Mem" value={unit.memory || "—"} />
          <Spec label="SSD" value={unit.storage || "—"} />
          <Spec label="Batt" value={batt} sub={unit.battery_cycles != null ? `${unit.battery_cycles} cyc` : ""} />
          <Spec label="Year" value={String(unit.year || "—")} />
        </div>

        <div className="mt-3 flex items-center justify-between">
          {editingPrice ? (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <span className="text-sm text-[#98989d]">₹</span>
              <input
                data-testid={`inline-price-input-${unit.stock_code}`}
                type="number"
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") savePrice(e); if (e.key === "Escape") setEditingPrice(false); }}
                placeholder="Price"
                className="w-24 rounded-lg border border-white/10 bg-black px-2 py-1 text-sm outline-none focus:border-[#2997ff]"
              />
              <button data-testid={`inline-price-save-${unit.stock_code}`} onClick={savePrice} disabled={saving} className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#2997ff]">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              </button>
              <button onClick={(e) => { e.stopPropagation(); setEditingPrice(false); setValue(unit.price ?? ""); }} className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 text-[#98989d]">
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              data-testid={`inline-price-edit-${unit.stock_code}`}
              onClick={(e) => { e.stopPropagation(); if (!canPrice) return; setValue(unit.price ?? ""); setEditingPrice(true); }}
              className="flex items-center gap-1.5 rounded-lg px-1 -mx-1 text-left transition-colors hover:bg-white/5"
            >
              <span className="text-lg font-extrabold">
                {unit.price ? inr(unit.price) : <span className="text-sm font-medium text-[#98989d]">Ask for price</span>}
              </span>
              {canPrice && <Pencil size={13} className="text-[#98989d]" />}
            </button>
          )}
          {serviceDue && !editingPrice && (
            <span className="flex items-center gap-1 text-xs font-medium text-[#ff9f0a]">
              <BatteryWarning size={13} /> Service due
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function Spec({ label, value, sub }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[#98989d]">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
      {sub ? <div className="text-[9px] text-[#98989d]">{sub}</div> : null}
    </div>
  );
}
